import { useContext, useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { AuthContext } from '../../Context/AuthContext';

const ShareDataPage = () => {
  const { isAuthenticated, token } = useContext(AuthContext);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [recipients, setRecipients] = useState({
    doctor: false,
    insurance: false,
    family: false
  });

  useEffect(() => {
    const storedAssessments = localStorage.getItem('selectedAssessments');
    if (storedAssessments) {
      setSelectedAssessments(JSON.parse(storedAssessments));
      console.log(JSON.parse(storedAssessments));
    }
  }, []);

  const handleCheckboxChange = (key) => {
    setRecipients((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleManageContacts = () => {
    window.location.href = "/profile";
  };

  const handleSend = async () => {
    if (!isAuthenticated || !token) {
      alert('Please login first.');
      return;
    }

    // Map UI keys to backend personType values
    const categoryMap = { doctor: 'doctor', insurance: 'insurance', family: 'friend' };
    const selectedCategories = Object.entries(recipients)
      .filter(([, checked]) => checked)
      .map(([key]) => categoryMap[key]);

    if (selectedCategories.length === 0) {
      alert('Please select at least one recipient category.');
      return;
    }

    // Fetch emails for each selected category from backend
    try {
      const base = import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION;
      const requests = selectedCategories.map((personType) =>
        fetch(`${base}/careteam/${personType}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        }).then(async (res) => {
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message || `Failed to load ${personType} emails`);
          }
          return res.json();
        })
      );

      const results = await Promise.all(requests);
      // results is an array of arrays of { emailAddress, personType, enableEmail, ... }
      const allEmails = results
        .flat()
        .filter((r) => r?.emailAddress && (r?.enableEmail !== false)) // honor disable flag if present
        .map((r) => String(r.emailAddress).trim().toLowerCase());

      const uniqueEmails = Array.from(new Set(allEmails));

      if (uniqueEmails.length === 0) {
        alert('No recipient emails found for the selected categories. Please add contacts in your profile.');
        return;
      }

    // Build email body using chosen assessments
    const emailBody = `
      Hello,

      The following assessments have been selected to share with you:

      ${selectedAssessments.map(assessmentObj => `
      Assessment ID: ${assessmentObj._id}
      Mobile: ${assessmentObj.phone || assessmentObj.patientPhone || assessmentObj.mobile || 'N/A'}
      Assessment Name: ${assessmentObj.name || assessmentObj.assessment_name || assessmentObj.type || 'N/A'}
      Email: ${assessmentObj.email || assessmentObj.patientEmail || 'N/A'}
      Timestamp: ${assessmentObj.timestamp || assessmentObj.createdAt}

      Questions & Answers:
      ${assessmentObj.data.map(item => `
        Question: ${item.question}
        Answer: ${item.answer}
      `).join('\n')}
      `).join('\n\n')}

      Thank you,
      ${import.meta.env.TENANT_NAME || 'SmartNurse.ai'}
      `;

      const response = await fetch( base + '/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          recipients: uniqueEmails,
          subject: 'Shared Assessments',
          htmlBody: `<p>${emailBody.replace(/\n/g, '<br/>')}</p>`,  // Convert newlines to breaks for HTML body
          textBody: emailBody,
          fromEmail: 'info@smartnurse.ai' // make sure this is verified in your SES account
        })
      });

      const result = await response.json();
      if (!response.ok) {
        // If the response isn't OK, show an error
        alert(`Error sending email: ${result.message || 'Unknown error'}`);
      } else {
        alert('Emails have been sent successfully!');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('There was an error sending the email. Please try again later.');
    }
  };

  return (
    <>
      <Navbar />
      <div className="pt-10 min-h-screen flex flex-col items-center bg-gray-100">
        <div className="w-full max-w-4xl p-6 bg-white drop-shadow-lg hover:shadow-xl rounded-lg">
          <h1 className="text-xl font-bold mb-4">Share Data</h1>
          <p className="mb-4">Select a category to share data with the corresponding recipients:</p>
          
          {/* Manage Contacts Button */}
          <div className="mb-6">
            <button 
              className="w-full py-3 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600 transition-colors"
              onClick={handleManageContacts}
            >
              MANAGE CONTACTS (Add/Edit Friends, Doctors, Insurance)
            </button>
          </div>
          
          <div className="space-y-4">
            <div 
              className="bg-gradient-to-r from-white to-white p-4 shadow-lg hover:shadow-xl border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer"
              onClick={() => handleCheckboxChange('doctor')}
            >
              <div className="flex items-center text-black font-bold">
                <input 
                  type="checkbox" 
                  checked={recipients.doctor} 
                  onChange={(e) => e.stopPropagation()} 
                  className="mr-4" 
                />
                Doctors
              </div>
            </div>

            <div 
              className="bg-gradient-to-r from-white to-white p-4 shadow-lg hover:shadow-xl border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer"
              onClick={() => handleCheckboxChange('insurance')}
            >
              <div className="flex items-center text-black font-bold">
                <input 
                  type="checkbox" 
                  checked={recipients.insurance} 
                  onChange={(e) => e.stopPropagation()} 
                  className="mr-4" 
                />
                Insurance Company
              </div>
            </div>

            <div 
              className="bg-gradient-to-r from-white to-white p-4 shadow-lg hover:shadow-xl border border-gray-300 rounded-lg flex items-center justify-between cursor-pointer"
              onClick={() => handleCheckboxChange('family')}
            >
              <div className="flex items-center text-black font-bold">
                <input 
                  type="checkbox" 
                  checked={recipients.family} 
                  onChange={(e) => e.stopPropagation()} 
                  className="mr-4" 
                />
                Friends and Family
              </div>
            </div>
          </div>

          <button 
            className="w-full py-2 mt-6 bg-blue-500 text-white font-bold rounded-lg shadow-md hover:bg-blue-600 transition-colors"
            onClick={handleSend}
          >
            SEND
          </button>
        </div>
      </div>
    </>
  );
};

export default ShareDataPage;
